import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { api, extractErrorMessage } from '../api/client';
import { Modal } from './Modal';
import { Button } from './Button';
import { Badge } from './Badge';
import { useToast } from './Toast';

interface EstadoPlanilla { lastSyncAt: string | null; ok: boolean | null; rowsSynced: number; error: string | null }

const rtf = new Intl.RelativeTimeFormat('es-AR', { numeric: 'auto' });
/** "hace 5 minutos", "ayer"… */
function hace(iso: string): string {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  const [v, u]: [number, Intl.RelativeTimeFormatUnit] = s < 60 ? [s, 'second'] : s < 3600 ? [s / 60, 'minute'] : s < 86400 ? [s / 3600, 'hour'] : [s / 86400, 'day'];
  return rtf.format(-Math.round(v), u);
}
const formatearFechaSync = (v?: string | null) => (v ? new Date(v).toLocaleString('es-AR') : null);

/**
 * Trae la planilla 02_CODIGOS (publicaciones MLA → SKU y composición de cada SKU) con vista previa antes de escribir.
 * Vive en Configuración → Mercado Libre y catálogo. Desde la vista previa se cargan al maestro las «Variantes fuera del
 * maestro». La planilla también se trae sola cada 15 minutos.
 */
export function TraerPlanillaCodigos({ puedeTraer }: { puedeTraer: boolean }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data: codigosSyncStatus, refetch: refetchStatus } = useApi<EstadoPlanilla>('/api/v3/catalogo/codigos/sync/status');
  const [codigosPreview, setCodigosPreview] = useState<any>(null);
  const [codigosSyncing, setCodigosSyncing] = useState(false);
  const [variantesAAgregar, setVariantesAAgregar] = useState<Set<string>>(new Set());
  const [agregandoVariantes, setAgregandoVariantes] = useState(false);

  // Paso 1: simulación — no escribe nada, sólo trae el reporte con los números reales.
  const handleCodigosPreview = async () => {
    setCodigosSyncing(true);
    try {
      const r = await api.post<any>('/api/v3/catalogo/codigos/sync?dryRun=true', {});
      if (r?.abortado) {
        toast.addToast(r.motivoAborto || 'No se trajo la planilla: la frenó la guarda de seguridad', 'error');
        return;
      }
      setVariantesAAgregar(new Set());
      setCodigosPreview(r);
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setCodigosSyncing(false);
    }
  };

  // Carga inicial del maestro de variantes: da de alta las marcadas de la vista previa y la vuelve a pedir.
  const claveVarianteFuera = (d: { skuInterno: string; nombre: string }) => JSON.stringify([d.skuInterno, d.nombre]);
  const handleAgregarAlMaestro = async () => {
    const items = ((codigosPreview?.variantesFueraDelMaestroDetalle ?? []) as Array<{ skuInterno: string; nombre: string }>)
      .filter(d => variantesAAgregar.has(claveVarianteFuera(d)))
      .map(({ skuInterno, nombre }) => ({ skuInterno, nombre }));
    if (items.length === 0) return;
    setAgregandoVariantes(true);
    let agregadas = 0;
    const errores: string[] = [];
    let fallo: string | null = null;
    try {
      // En lotes de 500: un solo pedido grande superaría el límite del cuerpo del backend (100 kb).
      for (let i = 0; i < items.length; i += 500) {
        const r = await api.post<any>('/api/v3/catalogo/variantes/alta-masiva?dryRun=false', { items: items.slice(i, i + 500) });
        agregadas += (r?.nuevas?.length ?? 0) + (r?.cambios?.filter((c: any) => c.reactivar).length ?? 0);
        errores.push(...(r?.errores ?? []).map((e: any) => e.motivo));
      }
    } catch (e) {
      fallo = extractErrorMessage(e);
    }
    try {
      if (agregadas > 0) toast.addToast(`Se agregaron ${agregadas} variantes al maestro`, 'success');
      if (errores.length) toast.addToast(`No se pudieron agregar: ${errores.join('; ')}`, 'error');
      if (fallo) toast.addToast(fallo, 'error');
      await handleCodigosPreview();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setAgregandoVariantes(false);
    }
  };

  // Paso 2: ejecución real — recién acá se escribe en la base.
  const handleCodigosConfirmar = async () => {
    setCodigosSyncing(true);
    try {
      const r = await api.post<any>('/api/v3/catalogo/codigos/sync', {});
      if (r?.abortado) {
        toast.addToast(r.motivoAborto || 'No se trajo la planilla: la frenó la guarda de seguridad', 'error');
      } else {
        toast.addToast(
          `Planilla traída: ${r?.vinculosCreados ?? 0} nuevas, ${r?.vinculosReasignados ?? 0} reasignadas, ${r?.vinculosDesactivados ?? 0} desactivadas · ${(r?.skusCreadosDesdeSku ?? 0) + (r?.skusCreadosDesdeCod ?? 0)} SKU creados · ${r?.bomReemplazados ?? 0} composiciones`,
          'success',
        );
      }
      setCodigosPreview(null);
      refetchStatus();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setCodigosSyncing(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2">
      <h2 className="text-base font-semibold text-slate-800">Planilla 02_CODIGOS</h2>
      <p className="text-sm text-slate-600">Trae de la planilla de Google qué SKU va en cada publicación de Mercado Libre y de qué se compone cada SKU.</p>
      <p className="text-sm text-slate-700">
        {codigosSyncStatus?.lastSyncAt ? `Última: ${hace(codigosSyncStatus.lastSyncAt)}` : 'Todavía no se trajo'}
        {codigosSyncStatus?.lastSyncAt && (codigosSyncStatus.ok === false
          ? <Badge tone="red" className="ml-2">Con error</Badge>
          : <span className="text-slate-500">{` · ${codigosSyncStatus.rowsSynced.toLocaleString('es-AR')} filas`}</span>)}
      </p>
      {codigosSyncStatus?.error && <p className="text-xs text-red-700">{codigosSyncStatus.error}</p>}
      <p className="text-xs text-slate-500">Se trae sola cada 15 minutos.</p>
      <button
        onClick={handleCodigosPreview}
        disabled={codigosSyncing || !puedeTraer}
        className="text-brand-600 text-sm border border-brand-200 px-3 py-1.5 rounded-lg hover:bg-brand-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        title={!puedeTraer ? 'No tenés permiso para realizar esta acción' : 'Trae de la planilla 02_CODIGOS las publicaciones (MLA → SKU) y la composición de cada SKU'}
      >
        {codigosSyncing && !codigosPreview ? '⏳ Analizando...' : 'Traer planilla ahora'}
      </button>
        <Modal open={!!codigosPreview} onClose={() => setCodigosPreview(null)} title="Traer planilla 02_CODIGOS" size="lg">
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              La planilla define qué SKU va en cada publicación (MLA + variante) y de qué SKU se compone cada uno. <strong>No modifica SKU existentes</strong> (título, marca, proveedor, descripción ni estado): sólo crea los que faltan. Las publicaciones que ya no están en la planilla se <strong>desactivan</strong> (no se borran). Esta sincronización corre sola cada 15 minutos.
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
              {[['Publicaciones en la planilla', codigosPreview?.vinculosEnPlanilla ?? 0],
                ['Publicaciones nuevas', codigosPreview?.vinculosCreados ?? 0],
                ['Publicaciones reasignadas a otro SKU', codigosPreview?.vinculosReasignados ?? 0],
                ['Publicaciones reactivadas', codigosPreview?.vinculosReactivados ?? 0],
                ['Publicaciones con agrupador/variante nuevos', codigosPreview?.vinculosDatosActualizados ?? 0],
                ['Publicaciones a desactivar', codigosPreview?.vinculosDesactivados ?? 0],
                ['SKU a crear (columna SKU)', codigosPreview?.skusCreadosDesdeSku ?? 0],
                ['SKU a crear (componentes de la planilla)', codigosPreview?.skusCreadosDesdeCod ?? 0],
                ['Composiciones a reemplazar', codigosPreview?.bomReemplazados ?? 0],
                ['SKU con composición distinta entre filas', codigosPreview?.conflictosBom?.total ?? 0],
                ['Autorreferencias omitidas', codigosPreview?.autoReferenciasOmitidas?.total ?? 0],
                ['Publicaciones con autorreferencia (no se sincronizan)', codigosPreview?.publicacionesConAutorreferencia?.total ?? 0],
                ['Packs que empiezan a sincronizar con ML', codigosPreview?.packsHabilitados?.total ?? 0],
                ['Componentes omitidos por ciclo', codigosPreview?.ciclosOmitidos?.total ?? 0],
                ['Proveedores sin coincidencia', codigosPreview?.proveedoresSinMatch?.total ?? 0],
                ['Variantes fuera del maestro', codigosPreview?.variantesFueraDelMaestro?.total ?? 0],
                ['SKU sin composición en la planilla', codigosPreview?.skusSinComposicion ?? 0],
                ['Filas sin SKU (omitidas)', codigosPreview?.filasSinSku ?? 0]].map(([l, v]: any) => (
                <div key={l} className="rounded-lg border border-slate-200 p-3">
                  <div className="text-xs text-slate-500">{l}</div>
                  <div className="text-lg font-semibold text-slate-800">{v}</div>
                </div>
              ))}
            </div>
            <ul className="text-xs text-slate-500 list-disc pl-4 space-y-0.5">
              <li>Todo código de proveedor es un SKU interno con el mismo código: los cod que no existen se crean como SKU. A los SKU nuevos se les carga título (sólo los de la columna SKU), marca y proveedor estándar si coincide con uno existente.</li>
              <li>Una lista [SKU x1] significa producto simple: no lleva composición. Los SKU sin ningún cod en la planilla conservan su composición actual.</li>
              {(codigosPreview?.conflictosBom?.total ?? 0) > 0 && (
                <li>Cuando un SKU tiene composiciones distintas en distintas publicaciones, manda la primera fila con composición; conviene crear un SKU de pack. Ejemplos: {codigosPreview.conflictosBom.muestra.slice(0, 5).join(', ')}{codigosPreview.conflictosBom.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.autoReferenciasOmitidas?.total ?? 0) > 0 && (
                <li>SKU que se incluyen a sí mismos (p. ej. [SKU x2]): se omite ese componente; hace falta un SKU de pack. Ejemplos: {codigosPreview.autoReferenciasOmitidas.muestra.slice(0, 5).join(', ')}{codigosPreview.autoReferenciasOmitidas.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.publicacionesConAutorreferencia?.total ?? 0) > 0 && (
                <li>Sus {codigosPreview.publicacionesConAutorreferencia.total} publicaciones no se sincronizan con Mercado Libre hasta corregir la columna SKU de la planilla (p. ej. &quot;SKU-x12&quot;): {codigosPreview.publicacionesConAutorreferencia.muestra.slice(0, 5).join(', ')}{codigosPreview.publicacionesConAutorreferencia.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.packsHabilitados?.total ?? 0) > 0 && (
                <li>Publicaciones que pasan de un SKU que sincroniza a su pack: el pack empieza a sincronizar solo, así no se corta el stock en Mercado Libre. Ejemplos: {codigosPreview.packsHabilitados.muestra.slice(0, 5).join(', ')}{codigosPreview.packsHabilitados.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.ciclosOmitidos?.total ?? 0) > 0 && (
                <li>Componentes omitidos porque formarían un ciclo: {codigosPreview.ciclosOmitidos.muestra.slice(0, 5).join(', ')}{codigosPreview.ciclosOmitidos.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.proveedoresSinMatch?.total ?? 0) > 0 && (
                <li>Proveedores de la planilla que no coinciden con ninguno cargado (no se crean; el SKU nuevo queda sin proveedor): {codigosPreview.proveedoresSinMatch.muestra.slice(0, 8).join(', ')}{codigosPreview.proveedoresSinMatch.total > 8 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.variantesFueraDelMaestro?.total ?? 0) > 0 && (
                <li>Variantes de la planilla que no están en el maestro (no se crean ni se ligan; se cargan en la pantalla Variantes): {codigosPreview.variantesFueraDelMaestro.muestra.slice(0, 5).join(', ')}{codigosPreview.variantesFueraDelMaestro.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.variantesFueraDelMaestroDetalle?.length ?? 0) > 0 && (
                <li className="list-none -ml-4 space-y-1">
                  <p>Son variantes que la planilla 02_CODIGOS usa y todavía no están en el maestro. Las publicaciones que las usan quedan sin ligar hasta que las agregues. Agregarlas no cambia nada en Mercado Libre.</p>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setVariantesAAgregar(new Set(codigosPreview.variantesFueraDelMaestroDetalle.map(claveVarianteFuera)))}>Marcar todas</Button>
                    <Button size="sm" variant="secondary" onClick={() => setVariantesAAgregar(new Set())}>Desmarcar todas</Button>
                    <Button
                      size="sm"
                      onClick={handleAgregarAlMaestro}
                      disabled={variantesAAgregar.size === 0 || agregandoVariantes || !can('catalogo', 'editar')}
                      title={!can('catalogo', 'editar') ? 'No tenés permiso para realizar esta acción' : undefined}
                    >
                      {`Agregar al maestro (${variantesAAgregar.size})`}
                    </Button>
                  </div>
                  <div className="max-h-48 overflow-y-auto space-y-0.5">
                    {codigosPreview.variantesFueraDelMaestroDetalle.map((d: { skuInterno: string; nombre: string; ocurrencias: number }) => (
                      <label key={claveVarianteFuera(d)} className="flex items-center gap-2 text-xs">
                        <input
                          type="checkbox"
                          checked={variantesAAgregar.has(claveVarianteFuera(d))}
                          onChange={() => setVariantesAAgregar(prev => {
                            const sig = new Set(prev);
                            if (!sig.delete(claveVarianteFuera(d))) sig.add(claveVarianteFuera(d));
                            return sig;
                          })}
                        />
                        {`${d.skuInterno} · ${d.nombre} (${d.ocurrencias})`}
                      </label>
                    ))}
                  </div>
                </li>
              )}
              {(codigosPreview?.variantesConNombreAnterior?.total ?? 0) > 0 && (
                <li>Variantes con el nombre viejo en la planilla (se ligan igual a la variante renombrada; actualizá la columna Variantes de la planilla con el nombre actual del maestro): {codigosPreview.variantesConNombreAnterior.muestra.slice(0, 5).join(', ')}{codigosPreview.variantesConNombreAnterior.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.variantesSinLigar?.total ?? 0) > 0 && (
                <li>Variantes que no se pudieron ligar a un código físico único: {codigosPreview.variantesSinLigar.muestra.slice(0, 5).join(', ')}{codigosPreview.variantesSinLigar.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.variantesSinCod?.total ?? 0) > 0 && (
                <li>Variantes de la planilla sin su componente: {codigosPreview.variantesSinCod.muestra.slice(0, 5).join(', ')}{codigosPreview.variantesSinCod.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.variantesDistintasPorFila?.total ?? 0) > 0 && (
                <li>SKU con variante de la planilla distinta entre filas (se ignora y sigue la columna Variantes de la planilla): {codigosPreview.variantesDistintasPorFila.muestra.slice(0, 5).join(', ')}{codigosPreview.variantesDistintasPorFila.total > 5 ? '…' : ''}.</li>
              )}
              {(codigosPreview?.vinculosDuplicados?.total ?? 0) > 0 && (
                <li>{codigosPreview.vinculosDuplicados.total} publicaciones aparecen con más de un SKU: queda el de la primera fila.</li>
              )}
            </ul>
            {formatearFechaSync(codigosSyncStatus?.lastSyncAt) && (
              <p className="text-xs text-slate-500">Última vez que se trajo: {formatearFechaSync(codigosSyncStatus?.lastSyncAt)}{codigosSyncStatus?.error ? ` — con error: ${codigosSyncStatus.error}` : ` — ${codigosSyncStatus?.rowsSynced ?? 0} filas`}</p>
            )}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button onClick={() => setCodigosPreview(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancelar</button>
              <button onClick={handleCodigosConfirmar} disabled={codigosSyncing} className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-500 disabled:opacity-50 disabled:cursor-not-allowed">{codigosSyncing ? 'Trayendo...' : 'Traer planilla'}</button>
            </div>
          </div>
        </Modal>
    </div>
  );
}
